import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, FileUpIcon, BrainIcon, AlertTriangleIcon, SaveIcon, SendIcon, EyeIcon, ZapIcon } from 'lucide-react';
import { toast } from 'sonner';
// Step components
import Step1Brochure from '../../components/programs/ProgramForm/Step1Brochure';
import Step2Overview from '../../components/programs/ProgramForm/Step2Overview';
import Step3Details from '../../components/programs/ProgramForm/Step3Details';
import Step4SkillsExtraction from '../../components/programs/ProgramForm/Step4SkillsExtraction';
import Step4EnrollmentForm from '../../components/programs/ProgramForm/Step4EnrollmentForm';
import Step5Review from '../../components/programs/ProgramForm/Step5Review';
const CreateProgram: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Brochure
    brochureFile: null,
    aiExtracted: false,
    // Step 2: Overview
    programName: '',
    programType: '',
    description: '',
    learningOutcomes: [''],
    targetStudents: [],
    deliveryMode: '',
    // Step 3: Details
    duration: '',
    intakeDates: [],
    tuition: '',
    qualifications: [],
    accreditation: '',
    campusLocation: '',
    // Step 4: Skills Extraction
    extractedSkills: [],
    // Step 5: Enrollment Form
    formFields: [],
    // Step 6: Review
    status: 'Draft'
  });
  const steps = [{
    id: 1,
    name: 'Upload Brochure',
    icon: <FileUpIcon className="w-5 h-5" data-id="element-6048" />
  }, {
    id: 2,
    name: 'Program Overview',
    icon: <BrainIcon className="w-5 h-5" data-id="element-6049" />
  }, {
    id: 3,
    name: 'Program Details',
    icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-6050" />
  }, {
    id: 4,
    name: 'Skills Extraction',
    icon: <ZapIcon className="w-5 h-5" data-id="element-6051" />
  }, {
    id: 5,
    name: 'Enrollment Form',
    icon: <EyeIcon className="w-5 h-5" data-id="element-6052" />
  }, {
    id: 6,
    name: 'Review & Publish',
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-6053" />
  }];
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  const handleUpdateFormData = (stepData: Partial<typeof formData>) => {
    setFormData({
      ...formData,
      ...stepData
    });
  };
  const handleSubmit = (status: 'Draft' | 'Pending Approval' | 'Active') => {
    // In a real app, you would submit the form data to your backend here
    console.log('Submitting program with status:', status);
    console.log('Form data:', formData);
    // Navigate back to programs page
    navigate('/university/programs');
  };
  const handleSaveDraft = () => {
    console.log('Saving draft:', formData);
    // Here you would save the form data to your backend
    // For now, just show a notification
    toast.success('Draft saved successfully!', {
      duration: 3000,
      icon: <SaveIcon className="w-5 h-5" data-id="element-6054" />
    });
  };
  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Brochure formData={formData} updateFormData={handleUpdateFormData} data-id="element-6055" />;
      case 2:
        return <Step2Overview formData={formData} updateFormData={handleUpdateFormData} data-id="element-6056" />;
      case 3:
        return <Step3Details formData={formData} updateFormData={handleUpdateFormData} data-id="element-6057" />;
      case 4:
        return <Step4SkillsExtraction formData={formData} updateFormData={handleUpdateFormData} data-id="element-6058" />;
      case 5:
        return <Step4EnrollmentForm formData={formData} updateFormData={handleUpdateFormData} data-id="element-6059" />;
      case 6:
        return <Step5Review formData={formData} updateFormData={handleUpdateFormData} onSubmit={handleSubmit} data-id="element-6060" />;
      default:
        return null;
    }
  };
  return <Layout title="Create New Program" persona="University Coordinator" universityName="UAE Business School" data-id="element-6061">
      <div className="max-w-5xl mx-auto" data-id="element-6062">
        {/* Step progress - Improved UI */}
        <nav aria-label="Progress" className="mb-10" data-id="element-6063">
          <ol className="flex items-center justify-between w-full" data-id="element-6064">
            {steps.map((step, index) => <li key={step.id} className="relative flex flex-col items-center" data-id="element-6065">
                {/* Connecting line before */}
                {index > 0 && <div className="absolute top-4 right-full w-full h-0.5" aria-hidden="true" data-id="element-6066">
                    <div className={`h-0.5 ${currentStep > index ? 'bg-primary-500' : 'bg-gray-200'}`} style={{
                width: 'calc(100% - 16px)',
                marginLeft: '8px'
              }} data-id="element-6067" />
                  </div>}
                {/* Step indicator */}
                <button onClick={() => step.id < currentStep && setCurrentStep(step.id)} className={`relative flex h-8 w-8 items-center justify-center rounded-full z-10 
                    ${currentStep > step.id ? 'bg-primary-500 hover:bg-primary-600' : currentStep === step.id ? 'bg-primary-50 border-2 border-primary-500' : 'bg-white border-2 border-gray-300'} 
                    ${step.id < currentStep ? 'cursor-pointer' : 'cursor-default'}`} disabled={step.id >= currentStep} aria-current={currentStep === step.id ? 'step' : undefined} data-id="element-6068">
                  {currentStep > step.id ? <CheckCircleIcon className="h-5 w-5 text-white" aria-hidden="true" data-id="element-6069" /> : <span className={currentStep === step.id ? 'text-primary-500' : 'text-gray-500'} data-id="element-6070">
                      {step.icon}
                    </span>}
                  <span className="sr-only" data-id="element-6071">{step.name}</span>
                </button>
                {/* Step label */}
                <div className="text-center mt-2" data-id="element-6072">
                  <span className={`text-xs font-medium ${currentStep === step.id ? 'text-primary-500' : currentStep > step.id ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-6073">
                    {step.name}
                  </span>
                </div>
              </li>)}
          </ol>
        </nav>
        {/* Form content */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-6074">
          {renderStep()}
        </div>
        {/* Navigation buttons */}
        <div className="flex justify-between mt-6" data-id="element-6075">
          <button type="button" onClick={handlePrevious} disabled={currentStep === 1} className={`px-4 py-2 rounded-md flex items-center text-sm font-medium ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-6076">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-6077" />
            Previous
          </button>
          <div className="flex space-x-3" data-id="element-6078">
            {/* Save Draft button available on all steps */}
            <button type="button" onClick={handleSaveDraft} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-gray-50" data-id="element-6079">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-6080" />
              Save Draft
            </button>
            {currentStep === steps.length /* Review step buttons */ ? <>
                <button type="button" onClick={() => handleSubmit('Pending Approval')} className="bg-gray-800 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-gray-700" data-id="element-6081">
                  <SendIcon className="w-4 h-4 mr-2" data-id="element-6082" />
                  Submit for Approval
                </button>
                <button type="button" onClick={() => handleSubmit('Active')} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-6083">
                  <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-6084" />
                  Publish Live
                </button>
              </> : currentStep === 5 /* Enrollment Form step - special case */ ? <button type="button" onClick={handleNext} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-6085">
                Continue to Review
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-6086" />
              </button> /* All other steps */ : <button type="button" onClick={handleNext} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-6087">
                Next
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-6088" />
              </button>}
          </div>
        </div>
      </div>
    </Layout>;
};
export default CreateProgram;